import { useCallback, useEffect } from "react";

const SLASH_SOUNDS = [
  "sounds/slash_1.wav",
  "sounds/slash_7.wav",
  "sounds/slash_8.wav",
  "sounds/slash_9.wav",
  "sounds/slash_10.mp3",
];

const VOLUME = 0.34;
const LOOKAHEAD_SECONDS = 0.005;

let audioContext: AudioContext | null = null;
let buffers: AudioBuffer[] = [];
let loadPromise: Promise<void> | null = null;
let fallbackIndex = 0;

function getAudioContext() {
  if (!audioContext) {
    const AudioContextCtor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) throw new Error("Web Audio is not supported.");
    audioContext = new AudioContextCtor();
  }
  return audioContext;
}

export function preloadSlashSounds() {
  if (loadPromise) return loadPromise;

  const context = getAudioContext();
  loadPromise = Promise.all(
    SLASH_SOUNDS.map(async (src) => {
      const response = await fetch(src);
      const data = await response.arrayBuffer();
      return context.decodeAudioData(data);
    })
  ).then((decoded) => {
    buffers = decoded;
  });

  return loadPromise;
}

function playFallbackAudio() {
  const audio = new Audio(SLASH_SOUNDS[fallbackIndex % SLASH_SOUNDS.length]);
  fallbackIndex += 1;
  audio.volume = VOLUME;
  audio.play().catch(() => {
    // Browsers can reject playback before user activation; gameplay can continue silently.
  });
}

export function useSlashSound() {
  useEffect(() => {
    preloadSlashSounds().catch(() => {
      buffers = [];
    });
  }, []);

  return useCallback(() => {
    const context = getAudioContext();

    if (context.state === "suspended") {
      context.resume().catch(() => undefined);
    }

    if (buffers.length === 0) {
      preloadSlashSounds().catch(() => undefined);
      playFallbackAudio();
      return;
    }

    const buffer = buffers[Math.floor(Math.random() * buffers.length)];
    const source = context.createBufferSource();
    const gain = context.createGain();

    source.buffer = buffer;
    gain.gain.value = VOLUME;
    source.connect(gain);
    gain.connect(context.destination);
    source.start(context.currentTime + LOOKAHEAD_SECONDS);
  }, []);
}
